import { PlanCapabilities } from "../models/organizations.entity";

export const DEFAULT_SEAT_TIERS = {
  tier_1_max: 50,
  tier_2_max: 500,
} as const;

type PlanVersionLike = {
  effectiveFrom: Date | string;
  effectiveTo?: Date | string | null;
  versionCode?: string;
  rulesJson: PlanCapabilities;
};

export function hasAnnualPricingTerms(capabilities?: PlanCapabilities): boolean {
  const terms = capabilities?.credit_policy?.commercial_terms;
  if (!terms) {
    return false;
  }

  if (terms.annual_subscription_price_cents) {
    return true;
  }

  const prices = terms.annual_price_per_seat_cents;
  if (!prices) {
    return false;
  }

  return prices.tier_1 > 0 || prices.tier_2 > 0 || prices.tier_3 > 0;
}

export function getAnnualPricePerSeatCentsFromCapabilities(
  capabilities: PlanCapabilities | undefined,
  seatCount: number,
): number {
  const terms = capabilities?.credit_policy?.commercial_terms ?? {};
  const normalizedSeatCount = Math.max(1, Math.floor(Number(seatCount) || 0));

  if (terms.annual_price_per_seat_cents) {
    const tiers = terms.seat_tiers ?? DEFAULT_SEAT_TIERS;
    const prices = terms.annual_price_per_seat_cents;

    if (normalizedSeatCount <= tiers.tier_1_max) {
      return prices.tier_1;
    }

    if (normalizedSeatCount <= tiers.tier_2_max) {
      return prices.tier_2;
    }

    return prices.tier_3;
  }

  return terms.annual_subscription_price_cents ?? 0;
}

function sortPlanVersionsByEffectiveFromDesc<T extends PlanVersionLike>(versions: T[]): T[] {
  return [ ...versions ].sort(
    (left, right) =>
      new Date(right.effectiveFrom).getTime() - new Date(left.effectiveFrom).getTime(),
  );
}

function isPlanVersionActiveAt(version: PlanVersionLike, at: Date): boolean {
  const effectiveFrom = new Date(version.effectiveFrom).getTime();
  const effectiveTo = version.effectiveTo ? new Date(version.effectiveTo).getTime() : null;
  const atTime = at.getTime();

  return effectiveFrom <= atTime && (effectiveTo === null || effectiveTo > atTime);
}

export function getLatestPlanVersion(
  versions: PlanVersionLike[] | undefined,
  at: Date = new Date(),
): PlanVersionLike | undefined {
  if (!versions?.length) {
    return undefined;
  }

  const activeVersions = versions.filter((version) => isPlanVersionActiveAt(version, at));
  if (activeVersions.length) {
    return sortPlanVersionsByEffectiveFromDesc(activeVersions)[0];
  }

  return sortPlanVersionsByEffectiveFromDesc(versions)[0];
}

export function resolvePlanVersionForCatalogPricing<T extends PlanVersionLike>(
  versions: T[] | undefined,
  at: Date = new Date(),
): T | undefined {
  if (!versions?.length) {
    return undefined;
  }

  const activeVersion = getLatestPlanVersion(versions, at) as T | undefined;
  if (activeVersion && hasAnnualPricingTerms(activeVersion.rulesJson)) {
    return activeVersion;
  }

  const currentVersion = sortPlanVersionsByEffectiveFromDesc(
    versions.filter((version) => version.versionCode === "current"),
  )[0];
  if (
    currentVersion
    && hasAnnualPricingTerms(currentVersion.rulesJson)
    && new Date(currentVersion.effectiveFrom).getTime() <= at.getTime()
  ) {
    return currentVersion;
  }

  if (activeVersion) {
    return activeVersion;
  }

  return sortPlanVersionsByEffectiveFromDesc(versions)[0];
}
